import type { DatasetUploadFailure, DatasetUploadStatus, UploadStatusGroup } from "@/api";
import { requiresUploadSourceChange } from "@/upload";

// import 목록 상태 필터 어휘. UI 라벨 목록이다. group 을 status 로 펼치는 일은 backend 가
// 소유한다(§3 `?status_group=`). 그래서 FE 는 라벨만 가지고, 선택한 group 값을 그대로 서버에 보낸다.
// 표시(드롭다운, 조건 칩)는 DatasetUploadFilterBar 가 이 어휘로 조립한다.
export const uploadStatusGroups: readonly { value: UploadStatusGroup; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "uploading", label: "업로드 중" },
  { value: "done", label: "완료" },
  { value: "failed", label: "실패" },
  { value: "cancelled", label: "취소" },
];

// 재시도할 수 있는 실패형 종료 상태. 더보기 "재시도" 액션의 표시 기준이다. duplicate_skipped 는
// 같은 데이터의 재임포트(hard-terminal)이므로 되돌릴 것이 없다. backend 가 reset·reprocess 를
// 모두 거부하므로 제외한다. 이 집합은 Failed 필터 그룹(가시성)과 다르다.
const retryableUploadStatuses: readonly DatasetUploadStatus[] = ["failed", "conflict"];

export function isRetryableUpload(status: DatasetUploadStatus): boolean {
  return retryableUploadStatuses.includes(status);
}

// 행을 지울 수 있는 종단 상태. backend `_DELETABLE` 과 같은 값이다. done 은 카탈로그가 확정된 실제
// 데이터셋이므로 backend 가 409 로 막는다. 진행 중인 잡도 같다. 삭제 권한은 별도 기준이므로
// 권한 판정은 호출부가 따로 한다.
const deletableUploadStatuses: readonly DatasetUploadStatus[] = [
  "failed",
  "cancelled",
  "conflict",
  "duplicate_skipped",
];

export function isDeletableUpload(status: DatasetUploadStatus): boolean {
  return deletableUploadStatuses.includes(status);
}

export type UploadRetryAction = "reprocess" | "reupload";

// 재시도 경로를 고른다. reprocess(업로드 바이트 재사용)는 backend 가 failed + upload_verified 에서만
// 허용한다(_REPROCESSABLE). 구조·경로 오류는 검증을 통과했어도 원본을 고쳐야 한다.
// conflict 는 reprocess 가 409 를 돌려준다. 그래서 두 경우 모두 폴더를 다시 선택한 뒤 reset 재업로드로 보낸다.
export function uploadRetryAction(
  status: DatasetUploadStatus,
  uploadVerified: boolean,
  failure: DatasetUploadFailure | null,
): UploadRetryAction {
  return status === "failed" && uploadVerified && !requiresUploadSourceChange(failure)
    ? "reprocess"
    : "reupload";
}
